import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { get, isArray, unset } from 'lodash';
import React, { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import { CreateFormProps, Field } from '../../../../lib/types/types';
import { fieldNameTranslation, isTrue, tooltipTranslate } from '../../../../utils/helper';
import FormFields from '../form-fields/FormFields';

const preprocessMarkdown = (text: any) => {
	return text.replace(/^(\d+)\./gm, '$1\\.'); // Wandelt "1." in "1\." um
};

const CreateTable: FC<CreateFormProps> = (props) => {
	const location = useLocation();
	const { formStruc, formValues, formErrors, updateFormValuesFunc, readOnly, columnsLabel, onDeleteFunc } = props;

	const allColumnsLabel = columnsLabel || [];

	const RenderCellTableHead: FC<{ eachColumn: string | Record<string, any> }> = ({ eachColumn }) => {
		if (typeof eachColumn === 'object') {
			return (
				<TableCell
					sx={{
						padding: '0.5rem',
						width: `calc(100%/${allColumnsLabel.length})`,
						minWidth: get(eachColumn, 'minWidth', '20rem'),
						maxWidth: get(eachColumn, 'maxWidth', 'auto'),
					}}
				>
					{' '}
					{get(eachColumn, 'label', '')}
				</TableCell>
			);
		} else {
			return <TableCell sx={{ padding: '0.5rem' }}> {eachColumn}</TableCell>;
		}
	};
	const RenderPinnedRightRow = (eachFieldRow: any, rowIndex: number) => {
		if (get(eachFieldRow, 'userProps.allowDuplicateRow')) {
			return (
				<TableCell className='pinned-right-column'>
					<Stack direction={'row'}>
						<IconButton onClick={() => handleDuplicateRow(eachFieldRow, rowIndex)} disabled={readOnly}>
							<AddIcon />
						</IconButton>
						<IconButton onClick={() => handleDeleteDuplicatedRow(rowIndex)} disabled={readOnly}>
							<Delete />
						</IconButton>
					</Stack>
				</TableCell>
			);
		}
	};

	// TODO: RenderCell noch optimieren
	const RenderCellTableBody: FC<{ fieldProps: Field[] }> = ({ fieldProps }) => {
		if (get(fieldProps, 'type', '') === 'label' || get(fieldProps, 'type', '') === '') {
			// translation the strapi-field-name
			const dataPathParts = get(fieldProps, 'path', '').split('.');
			const strapiFieldName = dataPathParts.length > 0 ? dataPathParts[dataPathParts.length - 1] : get(fieldProps, 'path', '');
			const apiName = get(props, 'api') ? get(props, 'api', '') : get(location, 'state.api');
			const labelTranlated = fieldNameTranslation(strapiFieldName, apiName);

			const fieldLabel: any = isTrue(get(fieldProps, 'hideLabel')) ? '' : get(fieldProps, 'label', labelTranlated);
			const toolTip = tooltipTranslate(strapiFieldName, apiName);

			return (
				<Stack direction={'row'} spacing={1} alignItems={'center'}>
					<ReactMarkdown>{preprocessMarkdown(fieldLabel)}</ReactMarkdown>

					{(get(fieldProps, 'tooltip') || toolTip) && (
						<Tooltip
							title={<ReactMarkdown>{get(fieldProps, 'tooltip', '') || toolTip}</ReactMarkdown>}
							arrow
							placement='top'
							sx={{ cursor: 'pointer' }}
						>
							<InfoIcon />
						</Tooltip>
					)}
				</Stack>
			);
		}

		return <></>;
	};

	// wird gerade nirgendswo verwendet
	// TODO: Soll noch verbessert werden. (Erstmal nicht anfassen)
	const handleDelete = async (rowIndex: number) => {
		if (isTrue(get(props, 'allowDeleteFormValues'))) {
			// Pfad von einem Feld, damit man dann die richtige Object zum Löschen in Antrag(objekt) finden kann
			let selectedRowFieldsFullPath = '';

			// jede Zelle in die Schleife gehen und die Pfade finden.
			// Es kann sein, dass die erste Splate nur als Info Text ist. Daher wird kein Pfad da sein.
			for (let i = 0; i < get(formStruc[rowIndex], 'fields', []).length; i++) {
				if (get(formStruc[rowIndex], `fields.${i}.type`)) {
					selectedRowFieldsFullPath = get(formStruc[rowIndex], `fields.${i}.path`, '');
					break;
				}
			}

			// Die letze attributes ausschneiden. Damit man die Pfad für den Object rausfinden kann, der man gerade zum löschen ausgewählt hat.
			const lastAttributesIndex = selectedRowFieldsFullPath.lastIndexOf('.attributes');
			const path = selectedRowFieldsFullPath.substring(0, lastAttributesIndex);
			// Nach dem pfad gefunden wurde, dann aus dem Antrag die Objekt rufen, die dann gelöscht wird.
			const toDeleteObject = get(formValues, `${path}`, {});

			// Diese Funktion ist im jeden Komponente extra definiert.
			// Weil man nicht weiß, welche "collection-Type" gelöscht werden soll, wird hier einfach das Object und die pfad zum Objekt als Parameter übergeben.
			// Path ist auch notwendig, wenn man dann den "toDeleteObject" aus dem Antrag raus löschen muss. Dafür ist die beiden Parameter immer notwendig.
			// Mit der Zeit bzw. Afo kann weitere Parameter hier geben. Deswegen wird hier die Parameter als Objekt übergeben
			// Prinzip ist genau das gleich wie die params in MUI-DateGrid component
			onDeleteFunc({ toDeleteObject, path });
		}
	};

	const handleDuplicateRow = (eachFieldRow: any, rowIndex: number) => {
		const formValuesAtPath = get(formValues, get(props, 'duplicatePath'), []);
		const selectedRowValue = get(formValues, `${get(props, 'duplicatePath', '')}.${rowIndex}`);
		get(eachFieldRow, 'userProps.pathsNotToDuplicate', []).forEach((eachPath: string) => {
			let path = '';
			if (eachPath.includes('.')) {
				let pathsArr = eachPath.split('.');
				path = pathsArr[pathsArr.length - 1];
			} else path = eachPath;
			unset(selectedRowValue, path);
		});
		const newArray = [...formValuesAtPath, selectedRowValue];
		updateFormValuesFunc(`${get(props, 'duplicatePath', '')}`, newArray);
	};

	const handleDeleteDuplicatedRow = (rowIndex: number) => {
		updateFormValuesFunc(`${get(props, 'duplicatePath', '')}`, removeItemAtIndex(get(formValues, `${get(props, 'duplicatePath')}`, []), rowIndex));
	};

	const removeItemAtIndex = (array: any[], index: number) => {
		return [...array.slice(0, index), ...array.slice(index + 1)];
	};

	return (
		<>
			<TableContainer>
				<Table className='editable-table'>
					{isArray(allColumnsLabel) && allColumnsLabel.length > 0 ?
						<TableHead>
							<TableRow>
								{allColumnsLabel.map((ele: string | Record<string, any>, i: number) => {
									return <RenderCellTableHead key={i} eachColumn={ele} />;
								})}
								{isTrue(get(props, 'allowDeleteFormValues')) && <TableCell className='pinned-right-column'>Aktion</TableCell>}
							</TableRow>
						</TableHead>
					:	null}
					<TableBody>
						{(formStruc || []).map((eachFieldRow: any, rowIndex: number) => {
							return (
								<TableRow key={rowIndex} sx={{ borderBottom: '1.5px solid rgba(224, 224, 224, 1)', verticalAlign: 'top' }}>
									{get(eachFieldRow, 'fields', []).map((fieldProp: any, columnIndex: number) => {
										return (
											<TableCell key={columnIndex} sx={{ borderBottom: 'unset', padding: '8px 8px 4px 8px' }}>
												{get(fieldProp, 'type', '') === 'label' || get(fieldProp, 'type', '') === '' ?
													<>
														<RenderCellTableBody fieldProps={fieldProp} />
													</>
												:	<>
														<FormFields
															type={get(fieldProp, 'type')}
															formValues={formValues}
															formErrors={formErrors}
															onChange={updateFormValuesFunc}
															readOnly={readOnly ? readOnly : false}
															hideLabel={true}
															hideCharacterLengthInfo={true}
															size={get(props, 'size', 'small')}
															{...fieldProp}
														/>
													</>
												}
											</TableCell>
										);
									})}
									{RenderPinnedRightRow(eachFieldRow, rowIndex)}
									{isTrue(get(props, 'allowDeleteFormValues')) && (
										<TableCell className='pinned-right-column'>
											<IconButton onClick={() => handleDelete(rowIndex)} disabled={readOnly}>
												<Delete />
											</IconButton>
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

export default CreateTable;
